/* eslint-disable import/no-extraneous-dependencies */
import React, {useEffect, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useDispatch} from 'react-redux'
import {makeStyles, TableBody, TableHead} from '@material-ui/core'
import Table from '@material-ui/core/Table'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {restrictToVerticalAxis} from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  changeProductArchiveStatusApi,
  ListProductType,
  switchProductPositionApi,
} from 'api/productApi'
import CardWithTitle from 'components/atoms/CardWithTitle'
import Pagination from 'components/molcules/Pagination'
import TableCell from 'components/Table/TableCell'
import AppLoading from 'components/atoms/Loading'
import useNoticeConfirm from 'features/modal/useNoticeConfirm'
import {updateStatus} from 'features/productManagement/productManagementSlice'
import {getProductDetailPath} from 'constants/RouteConstant'
import {
  PRODUCT_MANAGEMENT_TABLE_HEADER,
  SALE_STATUS,
  SaleStatus,
} from 'pages/ProductsPages/constant'
import {getQueryFromUrl} from 'helpers/commonHelper'
import ProductsManagementStyle from 'pages/ProductsPages/ProductsList/Style'
import useFailureModal from 'features/modal/useFailureModal'
import TableToolBar from 'pages/ProductsPages/ProductsList/TableToolBar'
import {AppDispatch} from 'store'
import useProductManagement from 'features/productManagement/useProductManagement'
import DraggableTableRow from './DraggableTableRow'

const ProductsManagement = () => {
  const {t} = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch<AppDispatch>()
  const useStyles = makeStyles(ProductsManagementStyle)
  const {onOpen: onNoticeDialogOpen} = useNoticeConfirm()
  const classes = useStyles()
  const {listProductPaging, loading, onSearchPaging} = useProductManagement()

  const [listProduct, setListProduct] = useState<ListProductType[]>([])

  const {onOpen: onFailureModalOpen} = useFailureModal()

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const location = useLocation()
  const searchParams = getQueryFromUrl(location.search)

  const handlePageChanged = (value: number) => {
    onSearchPaging({...searchParams, page: value})
  }
  const handlePageSizeChanged = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    onSearchPaging({
      ...searchParams,
      page: 0,
      size: parseInt(event.target.value as string, 10),
    })
  }

  const handleClickRow = (id: string) => {
    history.push(getProductDetailPath(id))
  }

  const onChangeSaleStatus = (
    event: React.MouseEvent<HTMLTableHeaderCellElement, MouseEvent>,
    id: string,
    currentStatus: SaleStatus,
  ) => {
    event.stopPropagation()
    let nextStatus: SaleStatus
    switch (currentStatus) {
      case SALE_STATUS.READY:
        nextStatus = SALE_STATUS.ON_SALE
        break
      case SALE_STATUS.ON_SALE:
        nextStatus = SALE_STATUS.OFF_SALE
        break
      case SALE_STATUS.OFF_SALE:
        nextStatus = SALE_STATUS.ON_SALE
        break
      default:
        nextStatus = SALE_STATUS.ON_SALE
        break
    }

    onNoticeDialogOpen({
      title: t(''),
      message: t('IChangeProductStatus'),
    })
      .then(async (result) => {
        if (result.payload) {
          try {
            await dispatch(updateStatus({goodsId: id, status: nextStatus}))
          } catch (err) {
            console.log(err)
          }
        }
      })
      .then(() => onSearchPaging(searchParams))
  }

  const handleDragRow = async (event: DragEndEvent) => {
    const {active, over} = event
    if (!over || active.id === over.id) return

    const oldIndex = listProduct.findIndex(
      (product) => product.goods.goodsId === active.id,
    )
    const newIndex = listProduct.findIndex(
      (product) => product.goods.goodsId === over.id,
    )
    try {
      const newItems = arrayMove(listProduct, oldIndex, newIndex)
      const movedIndex = newItems.findIndex(
        (product) => product.goods.goodsId === active.id,
      )
      const prevId = newItems[movedIndex - 1]?.goods.goodsId ?? null
      const nextId = newItems[movedIndex + 1]?.goods.goodsId ?? null
      await switchProductPositionApi({
        goodsId: String(active.id),
        previousGoodsId: prevId,
        nextGoodsId: nextId,
      })
      setListProduct(newItems)
    } catch (error) {
      onFailureModalOpen(t('IProcessFail'))
    }
  }

  const onChangeProductArchiveStatus = async (
    goodsId: string,
    isArchived: boolean,
  ) => {
    await changeProductArchiveStatusApi({
      goodsId,
      isArchived,
    })
    onSearchPaging(searchParams)
  }

  useEffect(() => {
    setListProduct(listProductPaging?.content || [])
  }, [listProductPaging])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragRow}
    >
      <AppLoading loading={loading} />
      <CardWithTitle
        title={t('IProductsManagement')}
        className={classes.cardTitle}
      >
        <TableToolBar />
        <Table size='small' className={classes.table}>
          <TableHead className={classes.tableHeader}>
            {PRODUCT_MANAGEMENT_TABLE_HEADER.map((item) => (
              <TableCell
                align='center'
                padding='checkbox'
                key={item.id}
                style={{
                  whiteSpace: 'pre',
                }}
              >
                {t(item.label)}
              </TableCell>
            ))}
          </TableHead>
          <SortableContext
            items={listProduct.map((item) => item.goods.goodsId)}
            strategy={verticalListSortingStrategy}
          >
            <TableBody>
              {listProduct.map((item, index) => (
                <DraggableTableRow
                  key={item.goods.goodsId}
                  item={item}
                  index={index}
                  onClickRow={handleClickRow}
                  onChangeSaleStatus={onChangeSaleStatus}
                  searchParams={searchParams}
                  productLength={listProduct.length}
                  onChangeProductArchiveStatus={onChangeProductArchiveStatus}
                />
              ))}
            </TableBody>
          </SortableContext>
        </Table>
        <Pagination
          totalPageCount={listProductPaging?.totalPages ?? 0}
          currentPageIndex={searchParams.page}
          itemCountPerPage={searchParams.size}
          setCurrentPageIndex={handlePageChanged}
          onItemCountPerPageChanged={handlePageSizeChanged}
          loading={loading}
        />
      </CardWithTitle>
    </DndContext>
  )
}

export default ProductsManagement
